import type { ComponentType } from 'react';
import type { Mode } from '@/types';
import { CreateAndPublishFlow } from '@/flows/CreateAndPublishFlow';
import { PlaybookPlayground } from './PlaybookPlayground';
import { ProjectPlayground } from './ProjectPlayground';
import { DocumentPlayground } from './DocumentPlayground';
import { MediaPlayground } from './MediaPlayground';
import { BrandKitPlayground } from './BrandKitPlayground';
import { ProfilesPlayground } from './ProfilesPlayground';
import { WebSocketPlayground } from './WebSocketPlayground';

export interface PlaygroundProps {
  mode: Mode;
}

export interface PlaygroundEntry {
  id: string;
  label: string;
  description?: string;
  status?: 'ready' | 'beta' | 'soon';
  /** Section grouping in the sidebar. */
  group: 'flows' | 'endpoints' | 'extras';
  component: ComponentType<PlaygroundProps>;
}

/**
 * The order in this array is the order shown in the sidebar (within each
 * group). Flows live at the top because they're the recommended path for
 * a developer's first contact with the API.
 */
export const PLAYGROUNDS: PlaygroundEntry[] = [
  // ── Guided flows ─────────────────────────────────────────────────────
  {
    id: 'flow-create-publish',
    label: 'Create & publish',
    description: 'End-to-end: project → upload → generate',
    group: 'flows',
    component: CreateAndPublishFlow,
  },
  // ── Single endpoints ─────────────────────────────────────────────────
  {
    id: 'project',
    label: 'Create Project',
    description: 'POST /media/projects',
    group: 'endpoints',
    component: ProjectPlayground,
  },
  {
    id: 'document',
    label: 'Upload Document',
    description: 'POST /media/projects/{id}/documents/{filename}',
    group: 'endpoints',
    component: DocumentPlayground,
  },
  {
    id: 'media',
    label: 'Generate Media',
    description: 'Storyboards & media generation',
    group: 'endpoints',
    component: MediaPlayground,
  },
  {
    id: 'brandkit',
    label: 'Brand Kits',
    description: 'GET / POST / PATCH brand kits',
    group: 'endpoints',
    component: BrandKitPlayground,
  },
  {
    id: 'playbook',
    label: 'Playbooks',
    description: 'Reusable saved_settings templates',
    group: 'endpoints',
    component: PlaybookPlayground,
  },
  {
    id: 'profiles',
    label: 'Voice / Face / Presenter',
    description: 'Profiles & cloning',
    group: 'endpoints',
    component: ProfilesPlayground,
  },
  // ── Extras / advanced ────────────────────────────────────────────────
  {
    id: 'websocket',
    label: 'WebSocket Events',
    description: 'Real-time status notifications',
    status: 'soon',
    group: 'extras',
    component: WebSocketPlayground,
  },
];

export const GROUPS: { id: 'flows' | 'endpoints' | 'extras'; label: string }[] = [
  { id: 'flows', label: 'Guided flows' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'extras', label: 'Extras' },
];
